import type { FolderTree } from '@/hooks/useFolders';
import { isUsRegion } from '@/jurisdictions/regions';

export type SourceFolderNode = {
  guid: string;
  name: string;
  children: SourceFolderNode[];
};

type SourceFolderTask = {
  created_by?: 'auto' | 'manual' | number | null;
  field_values?: Array<{
    is_manual?: boolean;
  }>;
  source_documents?: Array<{
    document_guid?: string | null;
    folder_guid?: string | null;
  }>;
};

function isManualSourceDeadline(task: SourceFolderTask): boolean {
  if (task.created_by && task.created_by !== 'auto') return true;
  return (task.field_values ?? []).some((fv) => fv.is_manual);
}

function normalizeFolderName(name: string): string {
  return name.trim().toLowerCase().replace(/judgements/g, 'judgments');
}

/** Default source-folder names for the matter's region and forum. */
export function defaultSourceFolderNames(region: string, forum: string | null): string[] {
  if (forum?.toLowerCase() === 'arbitration') {
    return ['Correspondence', 'Orders and Awards'];
  }
  if (isUsRegion(region)) {
    return ['Correspondence', 'Orders and Opinions'];
  }
  return ['Correspondence', 'Orders and Judgments'];
}

export function folderTreeToSourceNodes(folders: FolderTree[]): SourceFolderNode[] {
  return folders.map((folder) => ({
    guid: folder.guid,
    name: folder.name,
    children: folderTreeToSourceNodes(folder.children ?? []),
  }));
}

export function collectSubtreeGuids(folder: SourceFolderNode): string[] {
  return [folder.guid, ...folder.children.flatMap(collectSubtreeGuids)];
}

export function allSourceFolderGuids(folders: SourceFolderNode[]): string[] {
  return folders.flatMap(collectSubtreeGuids);
}

function folderMatchesDefault(folder: SourceFolderNode, defaultNames: Set<string>): boolean {
  return defaultNames.has(normalizeFolderName(folder.name));
}

function matchingGuidsInTree(folders: SourceFolderNode[], defaultNames: Set<string>): string[] {
  const matched: string[] = [];
  for (const folder of folders) {
    if (folderMatchesDefault(folder, defaultNames)) {
      matched.push(...collectSubtreeGuids(folder));
    } else {
      matched.push(...matchingGuidsInTree(folder.children, defaultNames));
    }
  }
  return matched;
}

/** GUIDs to pre-select: default-named folders and their descendants. */
export function defaultSourceFolderGuids(
  folders: SourceFolderNode[],
  region: string,
  forum: string | null,
): Set<string> {
  const defaultNames = new Set(defaultSourceFolderNames(region, forum).map(normalizeFolderName));
  return new Set(matchingGuidsInTree(folders, defaultNames));
}

/** Default-named top-level folders first (in region order), then the rest. */
export function partitionSourceFolders(
  folders: SourceFolderNode[],
  region: string,
  forum: string | null,
): { defaults: SourceFolderNode[]; rest: SourceFolderNode[] } {
  const defaultNames = defaultSourceFolderNames(region, forum).map(normalizeFolderName);
  const defaults: SourceFolderNode[] = [];
  const used = new Set<string>();
  for (const name of defaultNames) {
    const folder = folders.find((candidate) => normalizeFolderName(candidate.name) === name);
    if (folder && !used.has(folder.guid)) {
      defaults.push(folder);
      used.add(folder.guid);
    }
  }
  return {
    defaults,
    rest: folders.filter((folder) => !used.has(folder.guid)),
  };
}

/**
 * When `allowedFolderGuids` is null there is no folder restriction.
 * An empty set restricts extracted deadlines (none match); manuals/unsourced still pass.
 */
export function deadlineMatchesSourceFolders(
  task: SourceFolderTask,
  allowedFolderGuids: Set<string> | null,
): boolean {
  if (allowedFolderGuids === null) return true;
  if (isManualSourceDeadline(task)) return true;
  const folderGuids = (task.source_documents ?? [])
    .map((source) => source.folder_guid)
    .filter((guid): guid is string => Boolean(guid));
  if (folderGuids.length === 0) return true;
  return folderGuids.some((guid) => allowedFolderGuids.has(guid));
}
